import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaSearch } from "react-icons/fa";
import AgreementDetails from "./AdminAgreementDetails";
import { useUser } from "../../../context/UserContext";
import config from "../../../config/config";

const formatToLocalTime = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })
    .format(date)
    .replace(/ at /i, " at ");
};

const AgreementArea = ({ user, toggleLeft }) => {
  const [agreements, setAgreements] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const mainContainerRef = useRef(null);
  const leftContainerRef = useRef(null);
  const rightContainerRef = useRef(null);
  const { agreementId } = useParams();
  const baseURL = config.API_BASE_URL;

  const selectedAgreementRef = useRef(null);
  const [agreementsPerPage, setAgreementsPerPage] = useState(3);

  useEffect(() => {
    toggleLeft();
    return () => {
      toggleLeft();
    };
  }, [toggleLeft]);

  useEffect(() => {
    const fetchAgreements = async () => {
      try {
        const response = await axios.get(
          `${baseURL}/v1/agreements/worker/${user.id}`
        );
        const sortedAgreements = response.data.sort((a, b) => {
          return new Date(b.agreementDate) - new Date(a.agreementDate);
        });
        setAgreements(sortedAgreements);

        if (agreementId) {
          const foundAgreement = response.data.find(
            (a) => a.agreementId === agreementId
          );
          if (foundAgreement) {
            setSelectedAgreement(foundAgreement);
          } else {
            setSelectedAgreement(null);
          }
        } else {
          setSelectedAgreement(null);
        }
      } catch (error) {
        console.error("Error fetching agreements:", error);
      }
    };

    fetchAgreements();
  }, [baseURL, agreementId, user.id]);

  useEffect(() => {
    setAgreementsPerPage(selectedAgreement ? 2 : 3);
  }, [selectedAgreement]);

  useEffect(() => {
    if (mainContainerRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const { width } = entry.contentRect;
          console.log("Width:", width);
          const agreementsPerPage = width > 900 ? 3 : 2;
          setAgreementsPerPage(agreementsPerPage);
        }
      });

      resizeObserver.observe(mainContainerRef.current);

      return () => {
        if (mainContainerRef.current) {
          resizeObserver.unobserve(mainContainerRef.current);
        }
      };
    }
  }, []);

  useEffect(() => {
    adjustContentHeight();
    window.addEventListener("resize", adjustContentHeight);
    return () => window.removeEventListener("resize", adjustContentHeight);
  }, []);

  const firstPage = () => setCurrentPage(1);
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const nextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const lastPage = () => setCurrentPage(totalPages);

  const handleAgreementClick = async (agreement) => {
    try {
      console.log(
        `Navigating to and fetching details for agreement ID: ${agreement.agreementId}`
      );
      const response = await axios.get(
        `${baseURL}/v1/agreements/${agreement.agreementId}`
      );
      setSelectedAgreement(response.data);
    } catch (error) {
      console.error(
        `Error fetching agreement details for agreement ID: ${agreement.agreementId}`,
        error
      );
    }
  };

  const handleBackToList = () => {
    setSelectedAgreement(null);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "PARTIALLY_SIGNED_PENDING":
        return (
          <span className="agreement-status partially-signed-pending">
            PARTIALLY SIGNED (Pending)
          </span>
        );
      case "VIEWED_PENDING":
        return (
          <span className="agreement-status viewed-pending">
            VIEWED (Pending)
          </span>
        );
      case "FULLY_SIGNED":
        return <span className="agreement-status signed">FULLY SIGNED</span>;
      default:
        return <span className="agreement-status unknown">Unknown</span>;
    }
  };

  const adjustContentHeight = () => {
    if (mainContainerRef.current) {
      const containerHeight = mainContainerRef.current.offsetHeight;
      leftContainerRef.current.style.height = `${containerHeight}px`;
      rightContainerRef.current.style.height = `${containerHeight}px`;
    }
  };

  const indexOfLastAgreement = currentPage * agreementsPerPage;
  const indexOfFirstAgreement = indexOfLastAgreement - agreementsPerPage;
  const currentAgreements = agreements
    .filter((agreement) =>
      agreement.jobTitle.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .slice(indexOfFirstAgreement, indexOfLastAgreement);

  const totalPages = Math.ceil(
    agreements.filter((agreement) =>
      agreement.jobTitle.toLowerCase().includes(searchTerm.toLowerCase())
    ).length / agreementsPerPage
  );
  const maxPageNumbersToShow = 5;
  const halfPageNumbersToShow = Math.floor(maxPageNumbersToShow / 2);

  const startPage = Math.max(1, currentPage - halfPageNumbersToShow);
  const endPage = Math.min(totalPages, currentPage + halfPageNumbersToShow);

  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const handlePageChange = (action) => {
    if (action === "previous" && currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    } else if (action === "next" && currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    } else if (typeof action === "number") {
      setCurrentPage(action);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const renderAgreementListItem = (agreement) => (
    <div className="agreement-item-content">
      <div className="agreement-title">
        <h4>
          {agreement.jobTitle.length > 30
            ? `${agreement.jobTitle.substring(0, 30)}...`
            : agreement.jobTitle}
        </h4>
      </div>
      <div className="agreement-section">
        <strong>Submitted :</strong>
        <span className="agreement-date">
          {formatToLocalTime(agreement.agreementDate)}
        </span>
      </div>
      <div className="agreement-section">
        <h5>Parties :</h5>
        <div className="agreement-parties">
          <div className="worker-name">
            Worker:{" "}
            <span>
              {user?.userType === "worker"
                ? `${user.firstName} ${user.lastName}`
                : user?.userType === "client"
                ? agreement.workerName
                : ""}
            </span>
          </div>
          <div className="client-name">
            Client: <span>{agreement.clientName}</span>
          </div>
        </div>
      </div>
      <div className="agreement-section">
        <strong>Location :</strong>
        <span className="agreement-location">{agreement.jobLocation}</span>
      </div>
      <div className="agreement-status-container">
        <strong>Status :</strong>
        <span className={`agreement-status ${agreement.status}`}>
          {getStatusLabel(agreement.status)}
        </span>
        {agreement.status === "VIEWED_PENDING" && agreement.viewedAt && (
          <div className="agreement-status-container">
            <strong>Viewed :</strong>
            <span className={`agreement-status ${agreement.status}`}>
              {formatToLocalTime(agreement.viewedAt)}
            </span>
          </div>
        )}
        {agreement.status === "FULLY_SIGNED" && agreement.clientSignedAt && (
          <div className="agreement-status-container">
            <strong>Signed :</strong>
            <span className={`agreement-status ${agreement.status}`}>
              {formatToLocalTime(agreement.clientSignedAt)}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      ref={mainContainerRef}
      className={`agreement-area-container-admin ${
        selectedAgreement ? "expanded" : ""
      }`}
    >
      <div
        ref={leftContainerRef}
        className="agreement-area-left-container-admin"
      >
        {!selectedAgreement && (
          <div className="agreement-search-input-container">
            <input
              type="text"
              className="agreement-search-input"
              placeholder="Search agreements"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <FaSearch className="wmsgs-search-icon" />
          </div>
        )}
        {selectedAgreement && (
          <div className="agreement-area-header">
            <button onClick={handleBackToList}>See All Agreements</button>
          </div>
        )}

        <div className="agreement-area-left-div-admin">
          {agreements.length === 0 ? (
            <p>No agreements have been found.</p>
          ) : (
            <ul className="agreement-list">
              {selectedAgreement && (
                <li
                  key={selectedAgreement.agreementId}
                  className="agreement-list-item selected"
                  onClick={() => handleAgreementClick(selectedAgreement)}
                >
                  {renderAgreementListItem(selectedAgreement)}
                </li>
              )}
              {currentAgreements
                .filter(
                  (agreement) =>
                    agreement.agreementId !== selectedAgreement?.agreementId
                )
                .map((agreement) => (
                  <li
                    key={agreement.agreementId}
                    className={`agreement-list-item-admin ${
                      selectedAgreement &&
                      selectedAgreement.agreementId === agreement.agreementId
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleAgreementClick(agreement)}
                  >
                    {renderAgreementListItem(agreement)}
                  </li>
                ))}
            </ul>
          )}
        </div>
        {currentAgreements.length > 0 && (
          <div className="wm-list-pagination">
            <button onClick={firstPage} disabled={currentPage === 1}>
              &laquo;
            </button>
            <button onClick={prevPage} disabled={currentPage === 1}>
              &lt;
            </button>
            <span>
              Page <strong>{currentPage}</strong> of{" "}
              <strong>{totalPages}</strong>
            </span>
            <button onClick={nextPage} disabled={currentPage >= totalPages}>
              &gt;
            </button>
            <button onClick={lastPage} disabled={currentPage >= totalPages}>
              &raquo;
            </button>
          </div>
        )}
      </div>
      <div
        ref={rightContainerRef}
        className={`agreement-area-right-container-admin ${
          selectedAgreement ? "expanded" : ""
        }`}
      >
        <div ref={selectedAgreementRef} className="agreement-area-right-div">
          {selectedAgreement && (
            <AgreementDetails agreement={selectedAgreement} user={user} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AgreementArea;
